import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Col, Form, Row, Spin, message } from "antd";
import { Select } from "antd-v5";
import classNames from "classnames";
import AppLoader from "components/AppLoader";
import AuthorisedContainer from "components/AuthorisedContainer";
import { getVocabulary } from "components/Vocabulary";
import { ApproverProps, ApproversType } from "components/form/Approvers";
import { PermissionSetMap } from "constants/RBACConstants";
import { useActiveUserContext } from "contexts/ActiveUserContext";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import useAPIResponse from "hooks/useAPIResponse";
import _, { filter, find, isEmpty, isString, size, uniqBy } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ExceptionAPI from "restAPI/ExceptionAPI";
import TeamsAPI from "restAPI/TeamsAPI";
import UserAPI from "restAPI/UserAPI";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { UserResponseDto } from "types/api/userResponseDto";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  filterVocabularyOption,
  generateFormFields,
} from "utils/formUtils";

import ACButton from "ac-components/ac-button/ACButton";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";

import { useExceptionDetails } from "../ExceptionDetailsProvider";
import DraggableApproverItem from "./DraggableApproverItem";
import "./EditApprovalPopupV2.less";

function EditApprovalPopupV2({
  editId,
  afterClose,
}: {
  editId: number;
  afterClose: () => void;
}) {
  const [teamOptions, setTeamOptions] = useState<{ value: any; label: any }[]>(
    [],
  );
  const [form] = Form.useForm();
  const [exception, setException] = useState();
  const approversRef = useRef([]);
  const modalSettings = ACModalV2.useModalSettings();
  const { fetchProject } = useExceptionDetails();
  const userContext = useActiveUserContext();

  useEffect(() => {
    const { promise, cancel } = ExceptionAPI.get(editId);
    promise.then((response) => {
      const newCustomWorkflow = _.sortBy(response.customWorkflow, [
        "sequence",
      ]).map((a) => a.reviewerId || a.externalReviewerEmail);
      form.setFieldsValue({
        teamId: response.scope?.teamId,
        customWorkflow: newCustomWorkflow,
        subscribersUserIds: response.subscribersUserIds,
        approveByDate: response.approveByDate
          ? moment(response.approveByDate)
          : null,
      });
      setException(response);
    });
    return cancel;
  }, [form, editId]);

  const [allUsers, loading] = useAPIResponse(() => {
    const promiseObject = UserAPI.getAll();
    promiseObject.promise = promiseObject.promise
      .then((approvers) => {
        const userMap = approvers.map((d: { id: any; displayName: any }) => ({
          value: d.id,
          label: d.displayName,
        }));
        return userMap;
      })
      .finally(() => {});
    return promiseObject;
  }, []);

  useEffect(() => {
    //@ts-expect-error
    if (isEmpty(exception) || !exception?.scope?.productId) {
      setTeamOptions([]);
      return;
    }
    const { promise, cancel } = TeamsAPI.getProductSubProductTeams({
      //@ts-expect-error
      productId: exception.scope?.productId,
      //@ts-expect-error
      subProductId: exception.scope?.subProductIds,
    });
    promise.then((teams) => {
      setTeamOptions((prev) =>
        uniqBy(
          [
            ...(prev || []),
            ..._.map(teams, ({ team }) => ({
              value: team.id,
              label: team.name,
            })),
          ],
          "value",
        ),
      );
    });
    return cancel;
  }, [exception]);

  const fields = [
    {
      name: "teamId",
      label: getVocabulary("team"),
      type: FieldTypes.SELECT,
      options: teamOptions,
      loading: !teamOptions,
      colSpan: 12,
      //@ts-expect-error
      disabled: !exception?.scope?.productId,
    },
    {
      name: "customWorkflow",
      label: "",
      render: () => <Approvers approversRef={approversRef} />,
      colSpan: 24,
      style: { marginBottom: 10 },
      rules: [
        {
          validator: (rule: any, values: Array<number | string>) =>
            emailValidator(rule, values, userContext.sessionData),
        },
      ],
    },
    {
      name: "subscribersUserIds",
      label: "Send Updates To",
      type: FieldTypes.MULTI_SELECT,
      options: allUsers,
      loading: loading,
    },
    {
      name: "approveByDate",
      label: "Approve By Date",
      type: FieldTypes.DATE,
      colSpan: 12,
      props: {
        style: { width: "100%" },
      },
    },
  ];

  const onValuesChange = useCallback(
    (values) => {
      if (form) {
        if (_.has(values, "customWorkflow")) {
          form.setFieldsValue({ teamId: undefined });
        } else if (_.has(values, "teamId")) {
          form.setFieldsValue({
            customWorkflow: undefined,
            subscribersUserIds: undefined,
          });
          //@ts-expect-error
          if (values.teamId) {
            //@ts-expect-error
            const { promise } = TeamsAPI.getTeam(values.teamId);
            promise.then((res) => {
              if (size(res.approvalWorkflow?.approvers) > 0) {
                form.setFieldsValue({
                  customWorkflow: _.map(
                    _.sortBy(res.approvalWorkflow?.approvers, ["sequence"]),
                    (entry) => entry.reviewerId || entry.externalReviewerEmail,
                  ),
                  subscribersUserIds:
                    res.approvalWorkflow?.subscriberUserIds ?? [],
                });
              }
            });
          }
        }
      }
    },
    [form],
  );

  useEffect(() => {
    if (!exception) {
      return;
    }
    //@ts-expect-error
    if (exception.scope?.teamId) {
      // to fetch the team workflow on load
      //@ts-expect-error
      onValuesChange({ teamId: exception.scope.teamId });
    }
  }, [exception, onValuesChange]);

  const onSubmit = async () => {
    await form.validateFields();
    const values = form.getFieldsValue();
    //@ts-expect-error
    const newData = { ...exception };
    const newCustomWorkflow = _.map(values.customWorkflow, (id, i) => {
      const userData = _.find(approversRef.current, { value: id });
      const isEmail = typeof id === "string" && (id as string).includes("@");
      return {
        [isEmail ? "externalReviewerEmail" : "reviewerId"]: id,
        //@ts-expect-error
        reviewerType: isEmail ? "EXTERNAL_USER" : userData?.type || "USER",
        sequence: i,
      };
    });
    newData.startDate = moment(newData.startDate);
    newData.endDate = moment(newData.endDate);
    newData.approveByDate = values.approveByDate;
    newData.subscribersUserIds = values.subscribersUserIds;
    if (values.teamId) {
      newData.scope = {
        ...newData.scope,
        teamId: values.teamId,
      };
      delete newData.customWorkflow;
    } else if (hasPermission(PermissionSetMap.MODIFY_APPROVERS_LIST)) {
      delete newData.scope?.teamId;
      await ExceptionAPI.updateCustomWorkflow(newData.id, {
        customWorkflow: newCustomWorkflow,
      }).promise;
    }
    await ExceptionAPI.update(newData.id, newData).promise;
    fetchProject();
    modalSettings.close();
    afterClose?.();
    message.success("Successfully Updated");
  };

  return (
    <>
      <ACModalV2.Title>
        <div>Edit Approval Workflow</div>
      </ACModalV2.Title>

      <div className="p-x-l p-y-l" style={{ marginBottom: 64 }}>
        <Spin spinning={loading} indicator={<AppLoader />}>
          <Form layout="vertical" form={form} onValuesChange={onValuesChange}>
            {generateFormFields(fields)}
          </Form>
        </Spin>
      </div>

      <ACModalV2.Footer className="approval-form-footer">
        <ACButton type="outline" onClick={modalSettings.close}>
          Cancel
        </ACButton>
        <ACButton type="primary" onClick={onSubmit}>
          Save Changes
        </ACButton>
      </ACModalV2.Footer>
    </>
  );
}

type Props = ApproverProps & {
  dataSource?: ApproversType[];
};

export const Approvers = ({
  value = [],
  disableDND = false,
  onChange,
  approversRef,
  scrollY = 200,
  dataSource,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<boolean | string>(false);
  const [newApprovers, setNewApprovers] = useState<number[]>([]);
  const userContext = useActiveUserContext();
  const featureFlags = useFeatureFlag();
  const newApprovalFlow = featureFlags?.EXCEPTION_MODULE_UPDATE;

  const modifiedDataSource = useMemo(() => {
    return dataSource?.map((d) => ({
      ...d,
      label: `${d.label}${d.type === "ROLE" ? "(Role)" : ""}`,
    }));
  }, [dataSource]);

  const hasPermissionToUpdate = hasPermission(
    PermissionSetMap.MODIFY_APPROVERS_LIST,
  );

  const [allUsers] = useAPIResponse<UserInfoProjectorImpl[]>(
    UserAPI.getAll,
    [],
  );

  const [approvers] = useAPIResponse<ApproversType[]>(() => {
    setLoading(true);
    const promiseObject = ExceptionAPI.getApprovers();
    promiseObject.promise = promiseObject.promise
      .then((approvers) => {
        const userMap = approvers.map((d: UserResponseDto) => ({
          value: d.id,
          label: `${d.displayName}${d.type === "ROLE" ? "(Role)" : ""}`,
          type: d.type,
        }));
        // This is to avoid fetching approvers in parent component
        if (approversRef?.current) {
          approversRef.current = [...userMap];
        }
        return userMap;
      })
      .finally(() => {
        setLoading(false);
      });
    return promiseObject;
  }, []);

  const roles = useMemo(() => {
    return _.filter(
      modifiedDataSource || approvers,
      (entry) => entry.type === "ROLE",
    );
  }, [approvers, modifiedDataSource]);

  // Configure sensors for pointer (mouse/touch) and keyboard navigation
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const updateApprovers = useCallback(
    (currentOrder) => {
      onChange && onChange(currentOrder);
    },
    [onChange],
  );

  // Handle drag end event from dnd-kit
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = value.findIndex((v) => v === active.id);
      const newIndex = value.findIndex((v) => v === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const currentOrder = [...value];
        const [dragItem] = currentOrder.splice(oldIndex, 1);
        currentOrder.splice(newIndex, 0, dragItem);
        updateApprovers(currentOrder);
      }
    },
    [value, updateApprovers],
  );

  const onAddApprover = useCallback(async () => {
    try {
      await emailValidator(null, newApprovers, userContext.sessionData);
      updateApprovers(_.uniq([...value, ...(newApprovers || [])]));
      setNewApprovers([]);
      setEmailError(false);
    } catch (error) {
      setEmailError(true);
      console.warn("Invalid email domain");
    }
  }, [newApprovers, userContext.sessionData, updateApprovers, value]);

  const onRemoveApprover = useCallback(
    (removedUserId) => {
      updateApprovers(value.filter((userId) => userId !== removedUserId));
    },
    [value, updateApprovers],
  );

  const availableApproversList = useMemo(() => {
    return _.filter(
      modifiedDataSource || approvers,
      (approver) => value.indexOf(+approver.value) === -1,
    ).map((approver) => ({
      ...approver,
      title: approver.label,
    }));
  }, [approvers, value, modifiedDataSource]);

  const isApproversListEmpty = value.length === 0;

  const handleApproverChange = useCallback(
    (newApprovers) => {
      setNewApprovers(newApprovers);

      if (!newApprovers || newApprovers.length === 0) {
        setEmailError(false);
        return;
      }
      const newApproversIDs = newApprovers.map((i: string | number) =>
        isString(i) ? find(allUsers, { email: i })?.id : i,
      );

      //Validation for typed/selected approvers already chosen as approvers
      if (
        value.some(
          (v) => newApproversIDs.includes(v) || newApprovers.includes(v),
        )
      ) {
        const errorMessage =
          "Selected approvers must be different from existing ones";
        setEmailError(errorMessage);
        return;
      }
      //Validation for typed approvers already existing in the account
      if (
        !isEmpty(
          filter(
            newApprovers,
            (i: string | number) => isString(i) && find(allUsers, { email: i }),
          ),
        )
      ) {
        const errorMessage = "Selected approvers already exist in this account";
        setEmailError(errorMessage);
        return;
      }
      emailValidator(null, newApprovers, userContext.sessionData)
        .then(() => setEmailError(false))
        .catch(() => setEmailError(true));
    },
    [value, userContext.sessionData, allUsers],
  );

  return (
    <>
      <Row gutter={24} className="w-100">
        <Col span={12}>
          <div className="d-flex flex-column">
            <div>Add Approver</div>
            <div className="d-flex gap-8">
              <div className="flex-1 m-t-xs text-ellipsis">
                <AuthorisedContainer
                  permission={PermissionSetMap.MODIFY_APPROVERS_LIST}
                >
                  <Select
                    placeholder="Type name or email address"
                    className="w-100"
                    mode={newApprovalFlow ? "tags" : "multiple"}
                    loading={loading}
                    options={availableApproversList}
                    filterOption={filterVocabularyOption}
                    value={newApprovers}
                    onChange={handleApproverChange}
                  />
                </AuthorisedContainer>
              </div>
              <AuthorisedContainer
                permission={PermissionSetMap.MODIFY_APPROVERS_LIST}
              >
                <ACButton
                  type="outline"
                  style={{ width: 64, height: 32 }}
                  className="m-t-xs"
                  onClick={onAddApprover}
                  disabled={isEmpty(newApprovers) || Boolean(emailError)}
                >
                  Add
                </ACButton>
              </AuthorisedContainer>
            </div>
            {emailError && (
              <span className="danger">
                {isString(emailError) ? emailError : "Invalid email domain"}
              </span>
            )}
          </div>
        </Col>

        <Col span={12} className="approval-workflow-container">
          <div>Approvers</div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div
              className="approvers-scrollable-container"
              style={{ height: scrollY }}
            >
              {loading ? (
                <AppLoader />
              ) : (
                <SortableContext
                  items={value}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    style={{ padding: 2 }}
                    className={classNames({
                      "empty-state": isApproversListEmpty,
                    })}
                  >
                    {isApproversListEmpty && (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        Approval workflow not defined
                      </div>
                    )}
                    {value.map((entry, index) => {
                      const userDetail = _.find(allUsers, { id: entry });
                      let roleDetail: ApproversType | undefined;
                      if (!userDetail) {
                        //@ts-expect-error
                        roleDetail = _.find(roles, { value: entry });
                      }
                      return (
                        <DraggableApproverItem
                          key={entry}
                          id={entry}
                          approverData={entry}
                          index={index}
                          disabled={disableDND || !hasPermissionToUpdate}
                          onRemove={onRemoveApprover}
                          userDetail={userDetail}
                          roleDetail={roleDetail}
                          hasPermissionToUpdate={hasPermissionToUpdate}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              )}
            </div>
          </DndContext>
        </Col>
      </Row>
    </>
  );
};
export default EditApprovalPopupV2;

export const emailValidator = (
  rule: any,
  value: Array<number | string>,
  sessionData: any,
) => {
  let invalidEmail = false;

  let availableDomains: string[] = [];
  if (!_.isEmpty(sessionData?.currentOrgSessionDto?.domains)) {
    availableDomains = sessionData?.currentOrgSessionDto?.domains;
  } else {
    availableDomains = [sessionData.email.split("@")[1]];
  }
  _.forEach(value, (v) => {
    if (v && typeof v === "string") {
      const parts = v.split("@");
      if (parts.length !== 2 || !parts[0] || !parts[1]) {
        invalidEmail = true;
      } else if (!availableDomains.includes(parts[1])) {
        invalidEmail = true;
      }
    }
  });
  if (invalidEmail) {
    return Promise.reject("Invalid email domain");
  }
  return Promise.resolve();
};
